import { Injectable } from "@angular/core";
import { BehaviorSubject, ReplaySubject } from "rxjs";
import { CreatePropertyRequest } from "../models/CreatePropertyRequest.model";
import { PropertyFormState } from "../models/PropertyForm.state";
import { loadPropertyFormState } from "../utilities/PropertyFormStateLoader";
import { mapPropertyFormsToCreateRequest } from "../utilities/PropertyFormStateMapper";
import { Logger, LogLevel } from "../../../../infrastructure/logging/logger";
import { UserContextProvider } from "../../../../infrastructure/context/user-context-provider";
import { Country } from "../../../../shared/address/Country";
import { State } from "../../../../shared/address/State";
import { CountryService } from "../../../../shared/reference/country.service";
import { ReferenceService } from "../../../../shared/reference/reference.service";
import { PropertyApiService } from "../../services/property.api.service";

export interface PropertyFormError {
    message: string;
    error: String;
}

@Injectable()
export class PropertyFormService {

    private propertyId: number = 0;
    private states: BehaviorSubject<State[]> = new BehaviorSubject<State[]>([]);
    private countries: BehaviorSubject<Country[]> = new BehaviorSubject<Country[]>([]);

    private basicInformationState: any = {};
    private propertyAddressState: any = {};
    private photosState: { id: string, url: string }[] = [];
    private homeownerState: any = {};

    private addressStepValid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private basicInformationStepValid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private homeownerStepValid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    private existingPropertyLoaded: ReplaySubject<PropertyFormState> = new ReplaySubject<PropertyFormState>(1);
    private saveCompleted: ReplaySubject<number> = new ReplaySubject<number>(1);
    private error: ReplaySubject<PropertyFormError> = new ReplaySubject<PropertyFormError>();

    constructor(
        private referenceService: ReferenceService,
        private countryService: CountryService,
        private propertyApi: PropertyApiService,
        private userContextProvider: UserContextProvider,
        private logger: Logger
    ) {}

    async load(propertyId: number) {
        try {
            this.loading.next(true);
            this.propertyId = propertyId;
            this.loadStatesAndCountries();

            if (propertyId) {
                await this.loadPropertyFormState(propertyId);
            }
            this.loading.next(false);
        } catch (e) {
            let error;
            if(e instanceof Error) {
                error = e.message;
            } else {
                error = JSON.stringify(e);
            }

            this.error.next({ message: 'Failed to setup Property form', error });
            this.logger.handleError(e, 'An error occurred while retrieving Property form', 'Error while retrieving Property form');
            this.loading.next(false);
        }
    }

    private loadStatesAndCountries() {
        this.states.next(this.referenceService.getAllStates());

        this.countryService.getCountries()
            .then(countries => {
                if(this.userContextProvider.isNaUser()) {
                    countries = countries.filter(country => [1, 180, 244].includes(country.id));
                }
                
                this.countries.next(countries)
            })
            .catch(error => this.error.next({ message: 'Failed to load available countries', error: error }));
    }

    private async loadPropertyFormState(propertyId: number) {
        try {
            const property = await this.propertyApi.getPropertyDetail(propertyId);
            const state = loadPropertyFormState(property, this.states.getValue());
            if(state) {
                this.logger.log({ message: `Loaded property form state`, level: LogLevel.DEBUG });
                this.existingPropertyLoaded.next(state);
                this.photosState = state.photos;
            }
        } catch (e) {
            let error;
            if(e instanceof Error) {
                error = e.message;
            } else {
                error = JSON.stringify(e);
            }

            this.error.next({ message: 'Failed to load Property', error });
        }
    }

    async saveProperty(organizationId: number) {
        try {
            this.loading.next(true);

            let updatingProperty: boolean = false;
            if (this.propertyId > 0) {
                updatingProperty = true;
            }
            const request: CreatePropertyRequest = mapPropertyFormsToCreateRequest(
                this.basicInformationState,
                this.propertyAddressState,
                this.photosState,
                this.homeownerState,
                updatingProperty
            );
            if (!request) {
                this.error.next({ message: 'An error occurred while saving this Property', error: 'Could not map property form state to create request model' });
                return;
            }
    
            let propertyId;
            if (updatingProperty) {
                propertyId = await this.updateProperty(request);
            } else {
                request.organization_id = organizationId.toString();
                propertyId = await this.createProperty(request);
            }

            if(!propertyId) {
                return;
            }

            this.saveCompleted.next(propertyId);
        } catch (e) {
            let error;
            if(e instanceof Error) {
                error = e.message;
            } else {
                error = JSON.stringify(e);
            }
            this.error.next({ message: 'An error occurred while saving this Property', error });
        } finally {
            this.loading.next(false);
        }
    }

    private async updateProperty(request: CreatePropertyRequest): Promise<number | null> {
        const response = await this.propertyApi.updateProperty(this.propertyId, request);
        if(!response) {
            this.error.next({ message: 'An error occurred while saving this Property', error: 'Update Property response is null' });
            return null;
        }

        if(!response.property_id) {
            this.error.next({ message: 'An error occurred while saving this Property', error: 'PropertyId not found in update property response' });
            return null;
        }

        return response.property_id;
    }

    private async createProperty(request: CreatePropertyRequest): Promise<number | null> {
        const response = await this.propertyApi.createProperty(request);
        if(!response) {
            this.error.next({ message: 'An error occurred while saving this Property', error: 'Create Property response is null' });
            return null;
        }

        if(!response.property_id) {
            this.error.next({ message: 'An error occurred while saving this Property', error: 'PropertyId not found in create property response' });
            return null;
        }

        return response.property_id;
    }

    addPhoto(url: string = '', id?: string) {
        if(!id) {
            id = new Date().getMilliseconds().toString();
        }

        this.photosState.push({ id, url });
    }

    getPhotos(): { id: string, url: string }[] {
        return this.photosState;
    }

    deletePhoto(id: string) {
        this.photosState = this.photosState.filter(photo => photo.id !== id);
    }

    updatePhoto(id: string, url: string) {
        const photo = this.photosState.find(photo => photo.id === id);
        if(photo) {
            photo.url = url;
        }
    }

    isPhotosStepValid(): boolean {
        //Any number of photos is valid. Ignore empty inputs.
        return true;
    }

    isFormValid(): boolean {
        return this.addressStepValid.getValue() 
            && this.basicInformationStepValid.getValue() 
            && this.isPhotosStepValid();
    }

    updateBasicInformationState(state: any) {
        this.basicInformationState = state;
    }

    updatePropertyAddressState(state: any) {
        this.propertyAddressState = state;
    }

    updateHomeownerState(state: any) {
        this.homeownerState = state;
    }

    get states$(): BehaviorSubject<State[]> {
        return this.states;
    }

    get countries$(): BehaviorSubject<Country[]> {
        return this.countries;
    }

    get loading$(): BehaviorSubject<boolean> {
        return this.loading;
    }

    get error$(): ReplaySubject<{ message: string, error: String }> {
        return this.error;
    }

    get saveCompleted$(): ReplaySubject<number> {
        return this.saveCompleted;
    }

    get existingPropertyLoaded$(): ReplaySubject<PropertyFormState> {
        return this.existingPropertyLoaded;
    }

    setAddressStepValidity(valid: boolean) {
        this.addressStepValid.next(valid);
    }

    setBasicInformationStepValidity(valid: boolean) {
        this.basicInformationStepValid.next(valid);
    }

    setHomeownerStepValidity(valid: boolean) {
        this.homeownerStepValid.next(true);
    }

    addError(message: string, error: String) {
        this.error.next({ message: message, error: error });
    }
}